import React, { useState } from 'react';
import { useAuth } from '../context/AuthContext';
import { useTranslation } from 'react-i18next';

// --- HELPER COMPONENTS ---

const RoomStatusCard = ({ room, updateRoomStatus, loading }) => {
    const { t } = useTranslation();
    
    const getStatusColor = (status) => {
        switch (status) {
            case 'available': return 'var(--color-success)';
            case 'Needs Cleaning': return 'var(--color-error)';
            case 'Cleaning In Progress': return 'orange';
            case 'Inspecting': return 'blue';
            case 'Under Maintenance': return 'gray';
            case 'Out of Service': return 'darkred';
            case 'occupied': return 'var(--color-primary)';
            default: return 'var(--color-secondary)';
        }
    };

    // Formats the internal status key into a translated, readable label
    const formatStatus = (status) => {
        const key = status ? status.toLowerCase().replace(/ /g, '_') : 'na';
        return t(`housekeeping.statuses.${key}`).toUpperCase();
    };

    return (
        <div style={{
            border: `1px solid ${getStatusColor(room.status)}`,
            borderRadius: '8px',
            padding: '15px',
            marginBottom: '15px',
            backgroundColor: 'white',
            boxShadow: '0 2px 4px rgba(0,0,0,0.05)',
            minWidth: '200px'
        }}>
            <h4 style={{ margin: '0 0 10px 0', color: getStatusColor(room.status) }}>
                {t('housekeeping.room')} {room.roomNumber} ({room.type})
            </h4>
            <p style={{ margin: '0 0 10px 0', fontWeight: 'bold' }}>
                {t('housekeeping.current_status')}: <span style={{ color: getStatusColor(room.status) }}>{formatStatus(room.status)}</span>
            </p>
            
            <select
                value={room.status}
                onChange={(e) => updateRoomStatus(room.id, e.target.value)}
                style={{ width: '100%', padding: '8px', borderRadius: '4px', border: '1px solid #ccc' }}
                disabled={loading}
            >
                <option value="available">{t('housekeeping.statuses.available')}</option>
                <option value="Needs Cleaning">{t('housekeeping.statuses.needs_cleaning')}</option>
                <option value="Cleaning In Progress">{t('housekeeping.statuses.cleaning_in_progress')}</option>
                <option value="Inspecting">{t('housekeeping.statuses.inspecting')}</option>
                <option value="Under Maintenance">{t('housekeeping.statuses.under_maintenance')}</option>
            </select>
        </div>
    );
};


const LostAndFoundForm = ({ newLostItem = {}, setNewLostItem, handleRegisterLostItem, loading }) => {
    const { t } = useTranslation();

    const handleChange = (e) => {
        const { name, value } = e.target;
        if (typeof setNewLostItem === 'function') {
            setNewLostItem(prev => ({ ...prev, [name]: value }));
        }
    };

    const handleFileChange = (e) => {
        const file = e.target.files[0];
        if (file && typeof setNewLostItem === 'function') {
             setNewLostItem(prev => ({ 
                 ...prev, 
                 photoUrl: `temp-upload/${file.name}` 
             }));
        }
    };
    
    return (
        <div style={{ padding: '15px', border: '1px solid #ccc', borderRadius: '8px' }}>
            <h4 style={{ marginTop: 0 }}>{t('housekeeping.lost_found.register_title')}</h4>
            <form onSubmit={handleRegisterLostItem} style={{ display: 'grid', gap: '10px' }}>
                <input
                    type="text"
                    name="name"
                    value={newLostItem.name || ''}
                    onChange={handleChange}
                    placeholder={t('housekeeping.lost_found.item_name_placeholder')}
                    required
                    style={{ padding: '8px' }}
                />

                <textarea
                    name="description"
                    value={newLostItem.description || ''}
                    onChange={handleChange}
                    placeholder={t('housekeeping.lost_found.desc_placeholder')}
                    required
                    style={{ padding: '8px', minHeight: '60px' }}
                />
                
                <input
                    type="text"
                    name="roomNumber"
                    value={newLostItem.roomNumber || ''}
                    onChange={handleChange}
                    placeholder={t('housekeeping.lost_found.area_placeholder')}
                    required
                    style={{ padding: '8px' }}
                />

                <label style={{ fontSize: '0.8em', color: '#555', marginTop: '5px' }}>{t('housekeeping.lost_found.date_found_label')}:</label>
                <input
                    type="date"
                    name="dateFound"
                    value={newLostItem.dateFound || ''}
                    onChange={handleChange}
                    required
                    style={{ padding: '8px' }}
                />
                
                <input type="file" onChange={handleFileChange} style={{ padding: '8px' }} />
                
                {newLostItem.photoUrl && (
                    <p style={{ margin: '0 0 5px 0', fontSize: '0.9em' }}>
                        {t('housekeeping.lost_found.photo_uploaded')}: {newLostItem.photoUrl.substring(0, 20)}...
                    </p>
                )}
                
                <select
                    name="status"
                    value={newLostItem.status || 'Pending Retrieval'}
                    onChange={handleChange}
                    style={{ padding: '8px' }}
                >
                    <option value="Pending Retrieval">{t('housekeeping.lost_found.status_pending')}</option>
                    <option value="Claimed">{t('housekeeping.lost_found.status_claimed')}</option>
                    <option value="Archived">{t('housekeeping.lost_found.status_archived')}</option>
                </select>
                
                <button type="submit" className="btn-primary" disabled={loading} style={{ background: 'var(--color-primary)', color: 'white', border: 'none', padding: '10px', borderRadius: '4px', cursor: 'pointer' }}>
                    {t('housekeeping.lost_found.submit_btn')}
                </button>
            </form>
        </div>
    );
};

// --- MAIN COMPONENT ---
export default function HousekeepingDashboard({
    doNotDisturbRooms = [],
    rooms = [],
    lostItems = [],
    newLostItem = {}, 
    setNewLostItem,
    handleRegisterLostItem,
    handleUpdateLostItemStatus,
    updateRoomStatus,
    handleNotifyGuest,
    loading
}) {
    const { user } = useAuth();
    const { t } = useTranslation();
    const [filter, setFilter] = useState('Needs Cleaning');

    const sortedRooms = [...rooms].sort((a, b) => {
        if (a.status === 'Needs Cleaning' && b.status !== 'Needs Cleaning') return -1;
        if (a.status !== 'Needs Cleaning' && b.status === 'Needs Cleaning') return 1;
        return (a.roomNumber || "").localeCompare(b.roomNumber || "", undefined, { numeric: true });
    });

    const filteredLostItems = lostItems.filter(item => item.status === 'Pending Retrieval');

    return (
        <div style={{ marginBottom: '60px' }}>
            <div className="card">
                <h2>{t('housekeeping.title')}</h2>
                <p style={{ color: 'var(--color-secondary)' }}>
                    {user?.hotelName || 'Hotel'} - {t('housekeeping.subtitle')}
                </p>
            </div>

            <div className="section-title" style={{ marginTop: '40px' }}>
                🧹 {t('housekeeping.room_management_title')}
            </div>

            <div style={{ display: 'flex', gap: '10px', marginBottom: '20px' }}>
                <button onClick={() => setFilter('All')} style={{ padding: '8px 15px', backgroundColor: filter === 'All' ? 'var(--color-primary)' : 'gray', color: 'white', border: 'none', borderRadius: '4px', cursor: 'pointer' }}>
                    {t('housekeeping.filter_all')}
                </button>
                <button onClick={() => setFilter('Needs Cleaning')} style={{ padding: '8px 15px', backgroundColor: filter === 'Needs Cleaning' ? 'darkred' : 'gray', color: 'white', border: 'none', borderRadius: '4px', cursor: 'pointer' }}>
                    {t('housekeeping.statuses.needs_cleaning')}
                </button>
                <button onClick={() => setFilter('occupied')} style={{ padding: '8px 15px', backgroundColor: filter === 'occupied' ? '#3498db' : 'gray', color: 'white', border: 'none', borderRadius: '4px', cursor: 'pointer' }}>
                    {t('housekeeping.statuses.occupied')}
                </button>
            </div>

            <div style={{ display: 'flex', flexWrap: 'wrap', gap: '20px' }}>
                {sortedRooms
                    .filter(room => filter === 'All' || room.status === filter)
                    .map(room => (
                        <RoomStatusCard key={room.id} room={room} updateRoomStatus={updateRoomStatus} loading={loading} />
                    ))}
            </div>

            <div className="section-title" style={{ marginTop: '40px' }}>
                🚫 {t('housekeeping.dnd_title')} ({doNotDisturbRooms.length})
            </div>
            
            <div className="card" style={{ border: '1px solid #f39c12', backgroundColor: '#fff9e6' }}>
                {doNotDisturbRooms.length > 0 ? (
                    <ul style={{ listStyle: 'none', padding: 0 }}>
                        {doNotDisturbRooms.map(room => (
                            <li key={room.id} style={{ padding: '10px 0', borderBottom: '1px solid #ffe082' }}>
                                <strong>{t('housekeeping.room')} {room.roomNumber}</strong>: {t('housekeeping.dnd_message')}
                                <button
                                    onClick={() => handleNotifyGuest({ roomNumber: room.roomNumber, type: 'DND_CLEANING_ETA' })}
                                    style={{ marginLeft: '20px', backgroundColor: '#f39c12', color: 'white', border: 'none', padding: '5px 10px', borderRadius: '4px', cursor: 'pointer' }}
                                    disabled={loading}
                                >
                                    {t('housekeeping.send_eta_btn')}
                                </button>
                            </li>
                        ))}
                    </ul>
                ) : (
                    <p style={{ margin: 0, color: '#f39c12' }}>{t('housekeeping.no_dnd')}</p>
                )}
            </div>

            <div className="section-title" style={{ marginTop: '40px' }}>
                🔍 {t('housekeeping.lost_found_title')}
            </div>

            <div className="card" style={{ display: 'grid', gridTemplateColumns: '1fr 2fr', gap: '20px', marginBottom: '40px' }}>
                <LostAndFoundForm newLostItem={newLostItem} setNewLostItem={setNewLostItem} handleRegisterLostItem={handleRegisterLostItem} loading={loading} />
                
                <div style={{ padding: '15px', border: '1px solid #ccc', borderRadius: '8px' }}>
                    <h4 style={{ marginTop: 0 }}>{t('housekeeping.lost_found.pending_title')} ({filteredLostItems.length})</h4>
                    <div style={{ maxHeight: '300px', overflowY: 'auto' }}>
                        <ul style={{ listStyle: 'none', padding: 0 }}>
                            {filteredLostItems.length > 0 ? (
                                filteredLostItems.map(item => (
                                    <li key={item.id} style={{ borderBottom: '1px solid #eee', padding: '10px 0', display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap' }}>
                                        <div style={{ marginBottom: '5px', flexGrow: 1, minWidth: '150px' }}>
                                            <strong>{item?.name || t('common.na')}</strong> - {t('housekeeping.lost_found.found_in')} {item?.roomNumber || 'N/A'}
                                            <br/>
                                            <small style={{ color: 'gray' }}>{t('housekeeping.lost_found.reported')}: {item?.reportedDateDisplay || 'N/A'}</small>
                                        </div>
                                        <div style={{ display: 'flex', gap: '10px' }}>
                                            <button onClick={() => handleUpdateLostItemStatus(item.id, 'Claimed')} style={{ backgroundColor: 'green', color: 'white', padding: '5px 10px', border: 'none', borderRadius: '4px', cursor: 'pointer' }} disabled={loading}>
                                                {t('housekeeping.lost_found.mark_claimed')}
                                            </button>
                                            <button onClick={() => handleUpdateLostItemStatus(item.id, 'Archived')} style={{ backgroundColor: 'darkred', color: 'white', padding: '5px 10px', border: 'none', borderRadius: '4px', cursor: 'pointer' }} disabled={loading}>
                                                {t('housekeeping.lost_found.mark_archived')}
                                            </button>
                                        </div>
                                    </li>
                                ))
                            ) : (
                                <p style={{ color: 'gray' }}>{t('housekeeping.lost_found.no_items')}</p>
                            )}
                        </ul>
                    </div>
                </div>
            </div>
        </div>
    );
}